import styled, { css } from 'styled-components';
import { motion } from 'motion/react';
import { FrostedGlassStrong } from '@seed-health/tokens';
import {
  mqMediumMin,
  mqLargeMax,
  mqLargeMin,
} from 'styled/tools/media-queries';
import { FontFamily } from 'styled/fonts/font-family';
import { TypeBodyLarge } from 'components/library/Typography';

export type positions = 'left' | 'right';

export const ViaCapSection = styled.section`
  position: relative;
  padding-inline: var(--spacing-16);
  padding-block: var(--spacing-64);
  background-size: cover;
  overflow-x: hidden;

  @media ${mqMediumMin} {
    padding-inline: var(--spacing-24);
    padding-block: var(--spacing-72);
  }

  @media ${mqLargeMin} {
    padding-inline: var(--spacing-32);
    padding-block: var(--spacing-80);
  }
`;

export const ViaCapBackgroundVideo = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;

  mux-player {
    width: 100%;
    height: 100%;
    --controls: none;
    --media-object-fit: cover;
    --dialog: none;
  }
`;

/* Hidden under reduced motion in CSS rather than a render branch: the media query is
   unknown on the server, so branching in JSX would cause a hydration mismatch. */
export const ViaCapVideoControl = styled.div`
  position: absolute;
  right: var(--spacing-16);
  bottom: var(--spacing-16);
  z-index: 1;

  @media ${mqMediumMin} {
    right: var(--spacing-24);
  }

  @media ${mqLargeMin} {
    right: var(--spacing-32);
    bottom: var(--spacing-24);
  }

  /* The glass variant's focus ring matches its fill and disappears over the video. */
  button:focus-visible {
    outline-color: var(--actions-inverse);
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const ViaCapContainer = styled(motion.div)<{ $split?: boolean }>`
  border-radius: var(--radius-xl);
  background: rgba(87, 94, 85, 0.35);
  backdrop-filter: ${FrostedGlassStrong};
  -webkit-backdrop-filter: ${FrostedGlassStrong};
  padding: var(--spacing-16);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);
  align-items: center;
  margin-left: auto;
  margin-right: auto;
  max-width: 1440px;
  width: 100%;
  overflow: hidden;
  position: relative;

  @media ${mqMediumMin} {
    gap: var(--spacing-24);
  }

  @media ${mqLargeMax} {
    flex-direction: column;
    padding: var(--spacing-16);
  }

  /* Desktop: switch to row layout */
  @media ${mqLargeMin} {
    flex-direction: row;
    align-items: flex-start;
    padding: var(--spacing-80);
    gap: var(--spacing-32);
  }

  ${({ $split }) =>
    $split &&
    css`
      @media ${mqLargeMax} {
        padding: var(--spacing-32) var(--spacing-24) var(--spacing-24);
      }
    `}
`;

export const ViaCapContent = styled(motion.div)<{ $split?: boolean }>`
  & > * + * {
    margin-top: var(--spacing-24);
  }
  margin-bottom: var(--spacing-32);

  @media ${mqMediumMin} {
    margin-bottom: var(--spacing-40);
  }

  @media ${mqLargeMin} {
    flex: 0 1 45%;
    min-width: 0;
    margin-bottom: 0;
  }

  ${({ $split }) =>
    $split &&
    css`
      @media ${mqLargeMin} {
        align-self: stretch;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
    `}
`;

export const ViaCapBottomNote = styled.div`
  & > * + * {
    margin-top: var(--spacing-24);
  }

  max-width: 100%;

  @media ${mqLargeMax} {
    & > * + * {
      margin-top: var(--spacing-16);
    }
  }

  @media ${mqLargeMin} {
    margin-top: 57px;
  }
`;

export const ViaCapNote = styled(motion.div)<{ $pinToBottom?: boolean }>`
  padding: var(--spacing-16);
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  gap: var(--spacing-24);
  max-width: 380px;

  @media ${mqMediumMin} {
    padding: var(--spacing-24);
  }

  @media ${mqLargeMin} {
    padding: var(--spacing-32);
  }

  ${({ $pinToBottom }) =>
    $pinToBottom &&
    css`
      @media ${mqLargeMin} {
        && {
          margin-top: auto;
        }
      }
    `}
`;
export const NoteTitle = styled(TypeBodyLarge)`
  --fz: var(--font-size-450);
  color: var(--fill-primary);
  width: 190px;
`;

export const NotePill = styled.span`
  color: var(--fill-primary);
  font-family: var(--ff);
  font-size: var(--font-size-300);
  font-weight: 500;
  height: 24px;
  text-transform: uppercase;
  border-radius: var(--radius-full);
  border: 1.5px solid currentColor;
  padding-inline: var(--spacing-8);
  vertical-align: middle;
  display: inline-flex;
  align-items: center;
  margin-right: var(--spacing-8);
  margin-bottom: var(--spacing-4);
`;

export const NotePerf = styled(motion.p)`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * {
    font-family: ${FontFamily.sans};
    font-size: var(--font-size-800);
    font-weight: 300;
    color: var(--fill-primary);
  }

  & span > sup {
    color: var(--fill-primary);
  }
`;

export const NotePerfArrow = styled(motion.span)`
  font-family: ${FontFamily.sans};
  font-size: var(--font-size-800);
  font-weight: 300;
  color: var(--fill-primary);
  display: inline-block;
  font-feature-settings: 'ss03' on;
`;

export const NoteCount = styled.span`
  color: var(--fill-primary);
  font-family: ${FontFamily.sans};
  font-size: var(--font-size-800);
  font-weight: 300;
`;

export const TypeLabelLetter = styled(motion.span)`
  font-family: ${FontFamily.mono};
  font-size: var(--font-size-300);
  font-weight: 300;
  text-transform: uppercase;
  display: inline-block;
  color: var(--fill-primary);
`;

export const ViaCapWrapper = styled.div<{ small?: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  min-height: 400px;
  max-width: 100%;
  flex: 0 0 auto;
  width: 60%;
  margin-bottom: 0;
  padding: var(--spacing-32) 0;
  justify-content: space-between;

  @media ${mqMediumMin} {
    padding: var(--spacing-40) 0;
  }

  @media ${mqLargeMin} {
    padding: 57px 0;
    flex: 0 1 60%;
    min-width: 0;
  }
`;

export const Spacer = styled.div<{
  $width?: string;
}>`
  width: ${({ $width }) => $width || '100px'};
  flex-shrink: 0;
`;

export const ViaCapVideoContainer = styled.div<{ small?: boolean }>`
  position: absolute;
  z-index: 5;
  display: flex;
  justify-content: center;
  align-items: center;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 484px;
  height: 484px;
  pointer-events: none;
`;

export const ViaCapTextColumn = styled(motion.div)<{
  $position?: 'left' | 'right';
  $order?: number;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 0 1 auto;
  position: relative;
  z-index: 100;
  order: ${({ $order }) => $order};
  max-width: 200px;
  word-wrap: break-word;
  overflow-wrap: break-word;

  ${({ $position }) =>
    $position === 'left' &&
    `
    align-self: flex-start;
  `}

  ${({ $position }) =>
    $position === 'right' &&
    `
    align-self: flex-end;
    align-items: flex-start;
    margin-top: var(--spacing-16);
    padding: 0 var(--spacing-16);
  `}
`;

export const ViaCapNestedColumn = styled.div<{
  $position?: 'left' | 'right';
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  flex-shrink: 1;
  margin-top: -30px;
  margin-bottom: 96px;

  ${({ $position }) =>
    $position === 'left' &&
    `
    align-self: flex-start;
    justify-content: flex-start;
  `}

  ${({ $position }) =>
    $position === 'right' &&
    `
    align-self: flex-end;
    justify-content: flex-end;
  `}
`;

export const ViaCapDottedLine = styled.div<{
  $position?: 'left' | 'right';
  $alignSelf?: 'center' | 'flex-start' | 'flex-end';
}>`
  border-top: 1px dashed var(--fill-primary);
  opacity: 0.8;
  height: 1px;
  width: 70px;
  align-self: ${({ $alignSelf }) => $alignSelf};

  z-index: 10;
  transform: translateY(-50%);

  ${({ $position }) =>
    $position === 'left' &&
    `
    margin-top: var(--spacing-16);
  `}

  ${({ $position }) =>
    $position === 'right' &&
    `
    margin-bottom: var(--spacing-80);
  `}

  @media ${mqLargeMin} {
    margin-bottom: var(--spacing-48);

    ${({ $position }) =>
      $position === 'left' &&
      `
      margin-top: var(--spacing-48);
    `}

    ${({ $position }) =>
      $position === 'right' &&
      `
      transform: translateY(0);
      margin-top: var(--spacing-32);
    `}
  }
`;

export const ViaCapText = styled(motion.div)<{
  $position: positions;
  $small?: boolean;
}>`
  color: var(--fill-primary);
  font-family: ${FontFamily.sans};
  font-size: var(--font-size-300);
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  word-wrap: break-word;
  overflow-wrap: break-word;

  ${(props) =>
    props.$position == 'right' &&
    `
    align-items: flex-end;
    text-align: right;
  `}
`;

export const ViaCapSubhead = styled.h3`
  color: var(--fill-primary);
  font-size: var(--font-size-300);
  font-weight: 500;
  margin-block-end: 4px;
  text-transform: uppercase;
`;

export const ViaCapBorder = styled(motion.div)<{
  direction: string;
  small?: boolean;
}>`
  border-top: 1px dashed var(--fill-primary);
  opacity: 0.5;
  height: 1px;
  right: -50px;
  position: absolute;
  top: 10%;
  width: 70px;
  transform: scaleX(0);
  transform-origin: right;

  ${({ direction }) =>
    direction == 'right' &&
    `
    transform-origin: left;
    top: 15px;
    left: -65px;
    width: 50px;
  `}

  @media ${mqLargeMax} {
    height: 32px;
    width: 1px;
    border-left: 1px dashed var(--fill-primary);
    border-top: none;
    left: 50px;
    top: 110%;
    transform: scaleY(0);
    transform-origin: top;

    ${({ direction }) =>
      direction == 'right' &&
      `
        height: 42px;
        top: -55px;
        left: 75px;
        transform-origin: bottom;
    `}
  }
`;

const Shadow = css`
  &:after {
    content: '';
    background: transparent;
    border-radius: 99em 99em 0 0;
    box-shadow:
      0 76px 61px 0 rgba(0, 0, 0, 0.07),
      0 38px 30px 0 rgba(0, 0, 0, 0.05),
      0 23px 18px 0 rgba(0, 0, 0, 0.05),
      0 14px 11px 0 rgba(0, 0, 0, 0.04),
      0 9px 7px 0 rgba(0, 0, 0, 0.04),
      0 6px 5px 0 rgba(0, 0, 0, 0.03),
      0 3px 3px 0 rgba(0, 0, 0, 0.02),
      0 2px 1px 0 rgba(0, 0, 0, 0.02);
    height: 90%;
    inset: 5%;
    position: absolute;
    width: 90%;
  }
`;
export const ViaCapTop = styled(motion.div)`
  position: relative;
  z-index: 5;

  ${Shadow};

  @media ${mqLargeMax} {
    &:after {
      border-radius: 99em 0 0 99em;
    }
  }
`;

export const ViaCapMiddle = styled.div`
  position: relative;
  z-index: 1;
  ${Shadow};

  &:after {
    border-radius: 99em;
  }
`;

export const ViaCapBottom = styled(motion.div)`
  z-index: 3;
  ${Shadow};

  &:after {
    border-radius: 0 0 99em 99em;
  }

  @media ${mqLargeMax} {
    &:after {
      border-radius: 0 99em 99em 0;
    }
  }
`;

// Mobile-specific styled components for via-cap-element-small
export const MobileContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacing-32);
  width: 100%;
  min-height: 400px;

  @media ${mqMediumMin} {
    gap: var(--spacing-40);
  }

  @media ${mqLargeMin} {
    gap: 57px;
  }
`;

export const MobileColumn1 = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-24);
  flex: 1.5;
  max-width: 70%;
  z-index: 10;
  position: relative;
  padding-top: 32px;

  @media ${mqMediumMin} {
    gap: var(--spacing-32);
  }

  @media ${mqLargeMin} {
    gap: var(--spacing-40);
  }
`;

export const MobileColumn2 = styled.div`
  flex: 1;
  max-width: 35%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 100%;
  z-index: 1;
`;

export const MobileSolidLine = styled.div`
  height: 2px;
  background: var(--fill-primary);
  width: 100%;
  opacity: 0.8;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    background: var(--fill-primary);
    border-radius: 50%;
  }
`;

export const MobileTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);

  @media ${mqMediumMin} {
    gap: var(--spacing-24);
  }

  @media ${mqLargeMin} {
    gap: var(--spacing-32);
  }
`;
